import React, { useState, useEffect, useContext } from "react";
import { OrderListItem, OrderSearch } from "../models";
import { Link } from "react-router-dom";
import ordersService from "services/ordersService";
import tripService from "services/tripService";
import { Skeleton, Box, Paper, Tooltip, Typography, Stack, Button, Checkbox } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid";
import moment from "moment";
import ListFilter from "./ListFilter";
import { dateInPast, dateWithin90Mins } from "helpers/dateTimeMapping";
import { Role } from "common/Constants";
import { AuthContext } from "common/AuthProvider";
import WarningIcon from "@mui/icons-material/WarningAmber";
import AlarmIcon from "@mui/icons-material/Alarm";
import EditNotificationsIcon from "@mui/icons-material/EditNotifications";
import NotStartedOutlinedIcon from "@mui/icons-material/NotStartedOutlined";
import { toast } from "react-toastify";
import { useMaterialUIController } from "context";
import { readableLink, readableText } from "assets/theme/boldText";

const UncheckedIcon = () => (
  <Box sx={{ width: 18, height: 18, border: "2px solid rgba(0,0,0,0.6)", borderRadius: "3px" }} />
);

const OrderManagementList = () => {
  const [{ boldText }] = useMaterialUIController();
  const { isUserInRole, userHasPermission } = useContext(AuthContext);
  let isDispatcher: boolean = isUserInRole(Role.Dispatcher);
  const hasAllowApprove = userHasPermission("AllowApprove");
  const hasLitmusOverride = userHasPermission("AllowLitmusOverride");
  const hasBulkApproveUnverified = userHasPermission("AllowBulkApproveUnverified");

  // Load persisted filters from localStorage
  const getInitialSearch = (): OrderSearch => {
    const persistedFilters = localStorage.getItem('orderManagementFilters');
    const baseSearch: OrderSearch = {
      clientIds: null,
      hubIds: null,
      tripTypes: isDispatcher ? [{ id: 2, name: "RailRoad", value: "standard" }] : null,
      isCancelled: false,
      isClosed: null,
      isVerified: null,
      isApproved: null,
      isMpv: null,
      isMapped: null,
      isNearOtp: null,
      orderStatusIds: null,
      startDate: moment().subtract(7, "day").startOf('day').add(1, 'minute'),
      endDate: moment().endOf('day').subtract(1, 'minute'),
      alert: null,
      dispatchQueueConfigId: null,
      pagedSearchOptions: { offset: 0, pageSize: 25 },
      sortAsc: true,
      sortName: "tripID",
    };

    if (persistedFilters) {
      try {
        const parsed = JSON.parse(persistedFilters);
        // Merge persisted filters, dates included (closers were re-entering them on every visit).
        return {
          ...baseSearch,
          startDate: parsed.startDate ? moment(parsed.startDate) : baseSearch.startDate,
          endDate: parsed.endDate ? moment(parsed.endDate) : baseSearch.endDate,
          clientIds: parsed.clientIds || null,
          hubIds: parsed.hubIds || null,
          tripTypes: parsed.tripTypes || baseSearch.tripTypes,
          isCancelled: parsed.isCancelled ?? false,
          isClosed: parsed.isClosed ?? null,
          isVerified: parsed.isVerified ?? null,
          isApproved: parsed.isApproved ?? null,
          isMpv: parsed.isMpv ?? null,
          isMapped: parsed.isMapped ?? null,
          isNearOtp: parsed.isNearOtp ?? null,
          orderStatusIds: parsed.orderStatusIds || null,
          alert: parsed.alert || null,
          dispatchQueueConfigId: parsed.dispatchQueueConfigId || null,
          pagedSearchOptions: parsed.pagedSearchOptions || baseSearch.pagedSearchOptions,
          sortAsc: parsed.sortAsc ?? true,
          sortName: parsed.sortName || "tripID",
        };
      } catch (e) {
        console.error('Failed to parse persisted filters', e);
      }
    }
    return baseSearch;
  };

  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [search, setSearch] = useState<OrderSearch>(getInitialSearch());
  const [total, setTotal] = useState<number>(0);
  const [stageCounts, setStageCounts] = useState<{ [key: string]: number }>({});
  const [isLoading, setIsLoading] = useState(true);
  const gridRef = useGridApiRef();
  const [selectedTripIds, setSelectedTripIds] = useState<number[]>([]);
  const [allIdsLoaded, setAllIdsLoaded] = useState(false);
  const [bulkApproveLoading, setBulkApproveLoading] = useState(false);

  const handleSelectAllResults = async () => {
    try {
      const allIds = await ordersService.getAllTripIds(search);
      setSelectedTripIds(allIds);
      setAllIdsLoaded(true);
    } catch {
      toast.error("Failed to load all trip IDs");
    }
  };

  const handleBulkApprove = async () => {
    if (!selectedTripIds.length) return;
    setBulkApproveLoading(true);
    try {
      const results = await tripService.bulkApprove({ tripIds: selectedTripIds });
      const entries = Object.entries(results);
      const succeeded = entries.filter(([, r]: any) => r.isValid).length;
      const failures = entries.filter(([, r]: any) => !r.isValid);

      if (succeeded > 0) {
        toast.success(`Approved ${succeeded} trip(s)`);
      }
      if (failures.length > 0) {
        const failMessages = failures.map(([tripId, r]: any) => {
          const errors = r.errors?.map((e: any) => e.message).join(", ") || "Unknown error";
          return `Trip ${tripId}: ${errors}`;
        });
        toast.error(
          <div>
            <strong>{failures.length} trip(s) not approved:</strong>
            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
              {failMessages.map((msg, i) => <li key={i} style={{ fontSize: "0.85em" }}>{msg}</li>)}
            </ul>
          </div>,
          { autoClose: false }
        );
      }
    } catch {
      toast.error("Bulk approve failed");
    }
    setBulkApproveLoading(false);
    setSelectedTripIds([]);
    setAllIdsLoaded(false);
    setSearch({ ...search });
  };

  // Bulk approve requires AllowApprove + AllowLitmusOverride + Not Approved filter
  // Without AllowBulkApproveUnverified: also requires Verified filter set
  // With AllowBulkApproveUnverified: works regardless of verified filter
  const showBulkApprove = hasAllowApprove && hasLitmusOverride && search.isApproved === false && (
    hasBulkApproveUnverified || search.isVerified === true
  );

  const columns: GridColDef[] = [
    ...(showBulkApprove ? [{
      field: "select",
      headerName: "",
      width: 50,
      sortable: false,
      renderHeader: () => (
        <Checkbox
          size="small"
          icon={<UncheckedIcon />}
          checked={selectedTripIds.length > 0 && orders.every((o) => selectedTripIds.includes(o.tripID))}
          indeterminate={selectedTripIds.length > 0 && orders.some((o) => selectedTripIds.includes(o.tripID)) && !orders.every((o) => selectedTripIds.includes(o.tripID))}
          onChange={(e) => {
            if (e.target.checked) {
              const pageIds = orders.map((o) => o.tripID);
              setSelectedTripIds((prev) => [...new Set([...prev, ...pageIds])]);
            } else {
              const pageIds = new Set(orders.map((o) => o.tripID));
              setSelectedTripIds((prev) => prev.filter((id) => !pageIds.has(id)));
              setAllIdsLoaded(false);
            }
          }}
        />
      ),
      renderCell: (params) => (
        <Checkbox
          size="small"
          icon={<UncheckedIcon />}
          checked={selectedTripIds.includes(params.row.tripID)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedTripIds((prev) => [...prev, params.row.tripID]);
            } else {
              setSelectedTripIds((prev) => prev.filter((id) => id !== params.row.tripID));
            }
          }}
        />
      ),
    } as GridColDef] : []),
    { field: "alerts", headerName: "Alerts", renderCell: (r) => alertsRender(r.row), sortable: false, width: 60 },
    {
      field: "tripID",
      headerName: "Id",
      width: 100,
      renderCell: (params: GridRenderCellParams<any, number>) => (
        <Link to={`/trips/${params.value}`} target="_blank" rel="noopener noreferrer" style={readableLink(boldText)}>{params.value}</Link>
      ),
    },
    { field: "clientName", headerName: "Client", width: 100, hideable: true },
    { field: "clientType", headerName: "Type", width: 85, sortable: false, hideable: true },
    {
      field: "controlNumber",
      headerName: "Ctrl #/Short Key",
      flex: 1,
      sortable: false,
      renderCell: (r) =>
        r.row.shortKey ? (
          <Tooltip title={r.row.controlNumber}>
            <span className="table-cell-trucate">{r.row.shortKey}</span>
          </Tooltip>
        ) : (
          r.value
        ),
    },
    { field: "trainNumber", headerName: "Train", flex: 1 },
    { field: "hub", headerName: "Hub", width: 130 },
    {
      field: "pickupDateTime",
      headerName: "Pickup Time",
      flex: 1,
      minWidth: 165,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <>
          {`${
            params.value
              ? new Date(params.value).toLocaleString([], {
                  hourCycle: "h23",
                  year: "2-digit",
                  month: "numeric",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : ""
          }`}
          <Typography sx={{ fontSize: 11, ml: 0.25 }}>({params.row.timeZone?.trim()})</Typography>
        </>
      ),
    },
    { field: "minutesToPickup", headerName: "Min to Pickup", renderCell: (r) => minToPickupRender(r.row), width: 100, sortable: false },
    { field: "pickupLocation", headerName: "Pickup", flex: 1, renderCell: (r) => <Link to={`/points/${r.row.pickupPointId}`} style={readableLink(boldText)}>{r.row.pickupLocation}</Link> },
    { field: "dropoffLocation", headerName: "Dropoff", flex: 1, renderCell: (r) => <Link to={`/points/${r.row.dropoffPointId}`} style={readableLink(boldText)}>{r.row.dropoffLocation}</Link> },
    { field: "driverName", headerName: "Driver", flex: 1 },
    { field: "unitNumber", headerName: "Vehicle", width: 80 },
    { field: "mpv", headerName: "MPV", width: 60, renderCell: (params) => params.value ? "\u2713" : "" },
    ...(search.isNearOtp ? [{
      field: "otpMinutes",
      headerName: "OTP Min",
      width: 80,
      renderCell: (params) => {
        const val = params.value;
        if (val == null) return "";
        if (val <= 0) return <span style={{ color: "green" }}>{Math.abs(val)}m early</span>;
        return <span style={{ color: "red" }}>{val}m late</span>;
      },
    } as GridColDef] : []),
    ...(showBulkApprove ? [{
      field: "approveStatus",
      headerName: "Status",
      width: 90,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        const errors: string[] = [];
        if (!row.hasBeginTrip) errors.push("No Start");
        if (!row.hasPickupEvent) errors.push("No Pickup");
        if (!row.hasEndTrip) errors.push("No End");
        if (!row.isVerified && !hasBulkApproveUnverified) errors.push("Not Verified");
        if (row.isCancelled && !row.hasDriver) errors.push("No Driver");
        if (errors.length === 0) return <Tooltip title="Ready to approve"><span style={{ color: "green" }}>{"✓"} Ready</span></Tooltip>;
        return <Tooltip title={errors.join(", ")}><span style={{ color: "#d32f2f" }}>{"✗"} {errors.length} issue{errors.length > 1 ? "s" : ""}</span></Tooltip>;
      },
    } as GridColDef] : []),
    { field: "orderStatus", headerName: "Order Status", flex: 1, minWidth: 150 },
  ];

  const minToPickupRender = (row) => {
    if (row.hasArrivedAtPickupEvent) return "";
    // Show blank if pickup time is in the past
    if (dateInPast(row.pickupDateTime)) return "";
    switch (row.orderStatus) {
      case "Dispatch Review":
      case "New Order":
      case "Active Fulfillment":
        return row.minutesToPickup;
      default:
        return "";
    }
  };
  const alertsRender = (row) => {
    return (
      <>
        {!row.hasArrivedAtPickupEvent && dateInPast(row.pickupDateTime) && !row.isCancelled && (
          <Tooltip title="Late">
            <AlarmIcon color="error" />
          </Tooltip>
        )}
        {row.isAmended && (
          <Tooltip title="Amended">
            <EditNotificationsIcon color="info" />
          </Tooltip>
        )}
        {row.minutesToPickup <= 90 && row.minutesToPickup > 0 && (
          <Tooltip title="Pickup within 90mins">
            <WarningIcon color="warning" />
          </Tooltip>
        )}
        {row.isPreliminary && (
          <Tooltip title="Preliminary">
            <NotStartedOutlinedIcon color="action" />
          </Tooltip>
        )}
      </>
    );
  };
  useEffect(() => {
    setIsLoading(true);
    setOrders([]);
    setTotal(0);
    // A newer search supersedes this one: drop its late response so it can't overwrite the grid
    // (typing a date fires a search per keystroke, and an earlier, slower one could land last).
    let superseded = false;
    ordersService
      .searchOrders(search)
      .then((results) => {
        if (superseded) return;
        setOrders(results.results);
        setTotal(results.count);
        setStageCounts(results.stageCounts || {});
      })
      .finally(() => {
        if (!superseded) setIsLoading(false);
      });
    return () => {
      superseded = true;
    };
  }, [search]);

  // Persist filters to localStorage, dates included
  useEffect(() => {
    const filtersToPersist = {
      startDate: search.startDate,
      endDate: search.endDate,
      clientIds: search.clientIds,
      hubIds: search.hubIds,
      tripTypes: search.tripTypes,
      isCancelled: search.isCancelled,
      isClosed: search.isClosed,
      isVerified: search.isVerified,
      isApproved: search.isApproved,
      isMpv: search.isMpv,
      isMapped: search.isMapped,
      isNearOtp: search.isNearOtp,
      orderStatusIds: search.orderStatusIds,
      alert: search.alert,
      dispatchQueueConfigId: search.dispatchQueueConfigId,
      pagedSearchOptions: search.pagedSearchOptions,
      sortAsc: search.sortAsc,
      sortName: search.sortName,
    };
    localStorage.setItem('orderManagementFilters', JSON.stringify(filtersToPersist));
  }, [search.startDate, search.endDate, search.clientIds, search.hubIds, search.tripTypes, search.isCancelled, search.isClosed, search.isVerified, search.isApproved, search.isMpv, search.isMapped, search.isNearOtp, search.orderStatusIds, search.alert, search.dispatchQueueConfigId, search.pagedSearchOptions, search.sortAsc, search.sortName]);

  // When dispatch queue is selected, default sort to pickup time ascending and adjust dates
  useEffect(() => {
    if (search.dispatchQueueConfigId) {
      setSearch((prev) => ({
        ...prev,
        sortName: "pickupDateTime",
        sortAsc: true,
        startDate: moment().subtract(30, "minutes"),
        endDate: moment().add(1, "day").endOf("day"),
        pagedSearchOptions: { ...prev.pagedSearchOptions, offset: 0 },
      }));
      if (gridRef.current) {
        gridRef.current.setPage(0);
      }
    }
  }, [search.dispatchQueueConfigId]);

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (!(sortModel.length > 0)) return;
    var request = {
      ...search,
      sortName: sortModel[0].field,
      sortAsc: sortModel[0].sort == "asc",
      pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 },
    };
    setSearch(request);
    gridRef.current.setPage(0);
  };

  const handlePaginationModel = (pagination: GridPaginationModel) => {
    if (pagination.pageSize != search.pagedSearchOptions.pageSize) {
      pagination.page = 0;
    }
    setSearch({ ...search, pagedSearchOptions: { pageSize: pagination.pageSize, offset: pagination.page } });
  };

  const LoadingSkeleton = () => (
    <Box
      sx={{
        height: "fit-content",
        padding: "0px",
        margin: "0px",
        overflow: "hidden",
      }}
    >
      {[...Array(search.pagedSearchOptions.pageSize)].map((_, index) => (
        <Skeleton key={index} variant="rectangular" width="98%" sx={{ my: 3, mx: 1, overflow: "hidden" }} />
      ))}
    </Box>
  );

  const clearFilters = () => {
    // Clear persisted filters from localStorage
    localStorage.removeItem('orderManagementFilters');

    // Reset to fresh initial state without loading from localStorage
    const freshSearch: OrderSearch = {
      clientIds: null,
      hubIds: null,
      tripTypes: isDispatcher ? [{ id: 2, name: "RailRoad", value: "standard" }] : null,
      isCancelled: false,
      isClosed: null,
      isVerified: null,
      isApproved: null,
      isMpv: null,
      isMapped: null,
      isNearOtp: null,
      orderStatusIds: null,
      startDate: moment().subtract(7, "day").startOf('day').add(1, 'minute'),
      endDate: moment().endOf('day').subtract(1, 'minute'),
      alert: null,
      dispatchQueueConfigId: null,
      pagedSearchOptions: { offset: 0, pageSize: 25 },
      sortAsc: true,
      sortName: "tripID",
    };
    setSearch(freshSearch);
  };

  const getDispatchQueueRowColor = (row) => {
    // Only apply color coding when a dispatch queue is selected
    if (!search.dispatchQueueConfigId) {
      return "";
    }

    // Priority order: Red (late) > Blue (amended) > Grey (preliminary) > Orange (due soon) > Green (completed) > White (on-time)

    // Red: Trip is late (pickup time passed, not arrived, not cancelled, still in active dispatch statuses)
    const lateStatuses = ["Dispatch Review", "Active Fulfillment", "New Order"];
    if (
      !row.hasArrivedAtPickupEvent &&
      dateInPast(row.pickupDateTime) &&
      !row.isCancelled &&
      row.orderStatus &&
      lateStatuses.includes(row.orderStatus)
    ) {
      return "dispatch-queue-late";
    }

    // Blue: Trip has been amended
    if (row.isAmended) {
      return "dispatch-queue-amended";
    }

    // Grey: UP preliminary trip
    if (row.isPreliminary) {
      return "dispatch-queue-preliminary";
    }

    // Orange: Trip is due soon (within 90 mins)
    if (dateWithin90Mins(row.pickupDateTime) && !row.hasArrivedAtPickupEvent) {
      return "dispatch-queue-due-soon";
    }

    // Green: Trip is completed (arrived at pickup or past pickup time and not late)
    if (row.hasArrivedAtPickupEvent || (dateInPast(row.pickupDateTime) && !row.isCancelled)) {
      return "dispatch-queue-completed";
    }

    // White: Default (still time to make pickup)
    return "dispatch-queue-on-time";
  };

  return (
    <>
      <ListFilter gridRef={gridRef} search={search} setSearch={setSearch} clearFilters={clearFilters} totalCount={total} stageCounts={stageCounts}
        approveButton={showBulkApprove && selectedTripIds.length > 0 ? (
          <Button
            variant="contained"
            color="success"
            sx={{ whiteSpace: "nowrap" }}
            onClick={handleBulkApprove}
            disabled={bulkApproveLoading}
          >
            {bulkApproveLoading ? "Approving..." : `Approve (${selectedTripIds.length.toLocaleString()})`}
          </Button>
        ) : null}
      />
      {showBulkApprove && orders.every((o) => selectedTripIds.includes(o.tripID)) && orders.length > 0 && !allIdsLoaded && total > orders.length && (
        <Box sx={{ textAlign: "center", py: 0.5, backgroundColor: "#e3f2fd", borderLeft: "1px solid #e0e0e0", borderRight: "1px solid #e0e0e0" }}>
          <Typography variant="body2" component="span">
            {selectedTripIds.length} selected on this page.{" "}
            <Typography
              variant="body2"
              component="span"
              sx={{ color: "primary.main", cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}
              onClick={handleSelectAllResults}
            >
              Select all {total.toLocaleString()} results
            </Typography>
          </Typography>
        </Box>
      )}
      {allIdsLoaded && selectedTripIds.length > 0 && (
        <Box sx={{ textAlign: "center", py: 0.5, backgroundColor: "#e8f5e9", borderLeft: "1px solid #e0e0e0", borderRight: "1px solid #e0e0e0" }}>
          <Typography variant="body2" component="span">
            {selectedTripIds.length.toLocaleString()} of {total.toLocaleString()} results selected.{" "}
            <Typography
              variant="body2"
              component="span"
              sx={{ color: "primary.main", cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}
              onClick={() => { setSelectedTripIds([]); setAllIdsLoaded(false); }}
            >
              Clear selection
            </Typography>
          </Typography>
        </Box>
      )}
      <Box sx={{ overflow: "hidden" }}>
        <Paper sx={{ width: "100%", height: isLoading ? "100vh" : "auto", overflow: "hidden", borderRadius: "0 0 4px 4px" }}>
          <DataGrid
            autoHeight={isLoading ? false : true}
            getRowId={(row) => row.tripID}
            sx={{
              overflow: "hidden",
              fontSize: "1em",
              // 500 weight in black when the user turned on "Bold, darker text".
              ...readableText(boldText),
              display: "flex",
              flexDirection: "column-reverse",
              "& .MuiDataGrid-footerContainer": { order: 2, borderBottom: "1px solid #e0e0e0" },
              "& .MuiDataGrid-main": { order: 1 },
              "& .dispatch-queue-late": {
                backgroundColor: "#ffebee !important", // Light red
              },
              "& .dispatch-queue-amended": {
                backgroundColor: "#e3f2fd !important", // Light blue
              },
              "& .dispatch-queue-preliminary": {
                backgroundColor: "#f5f5f5 !important", // Grey
              },
              "& .dispatch-queue-due-soon": {
                backgroundColor: "#fff3e0 !important", // Light orange
              },
              "& .dispatch-queue-completed": {
                backgroundColor: "#C8E6C9 !important", // Light green
              },
              "& .dispatch-queue-on-time": {
                backgroundColor: "#ffffff !important", // White
              },
            }}
            rows={orders}
            columns={columns}
            pagination
            sortingMode="server"
            paginationMode="server"
            onPaginationModelChange={handlePaginationModel}
            onSortModelChange={handleSortModelChange}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  clientName: !isDispatcher,
                  clientType: !isDispatcher,
                },
              },
              pagination: { paginationModel: search.pagedSearchOptions },
              sorting: { sortModel: [{ field: "pickupTime", sort: "asc" }] },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            rowCount={total}
            disableColumnMenu
            slots={{
              loadingOverlay: LoadingSkeleton,
              noRowsOverlay: () => (
                <Stack height="100%" alignItems="center" justifyContent="center">
                  No Orders Match the filter criteria
                </Stack>
              ),
            }}
            loading={isLoading}
            density="compact"
            apiRef={gridRef as any}
            getRowClassName={(params) => {
              const colorClass = getDispatchQueueRowColor(params.row);
              const stripeClass = params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd";
              return `${stripeClass} ${colorClass}`;
            }}
          />
        </Paper>
      </Box>
    </>
  );
};
export default OrderManagementList;
